import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SBINModal from "../components/modals/SBINModal";
import { useAuth } from "../context/AuthContext";
import { normalizeSalonRole, roleLabel } from "../utils/salonRoles";
import {
  completeDailyTask,
  createDailyTask,
  deleteDailyTask,
  getDailyTasks,
  uncompleteDailyTask,
  updateDailyTask,
} from "../api/dailyTasks";

const ROLE_OPTIONS = [
  "owner",
  "manager",
  "receptionist",
  "technician",
  "apprentice",
  "independent_professional",
];

const VIEW_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "all", label: "All" },
  { value: "completed", label: "Completed" },
];

const SECTION_OPTIONS = [
  { value: "OPENING", label: "Opening" },
  { value: "DURING_DAY", label: "During the Day" },
  { value: "CLOSING", label: "Closing" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "PERSONAL", label: "Personal" },
];

const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "ONE_TIME", label: "One Time" },
];

const PRIORITY_OPTIONS = [
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

const DUE_TIME_HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0")
);
const DUE_TIME_MINUTES = ["00", "15", "30", "45"];
const DUE_TIME_PERIODS = ["AM", "PM"];
const pageText = "text-base md:text-lg";
const headerButton =
  "h-10 md:h-11 rounded-full px-5 text-sm md:text-base font-semibold transition";

const EMPTY_FORM = {
  title: "",
  section: "DURING_DAY",
  frequency_type: "DAILY",
  priority: "NORMAL",
  due_time: "",
  scope: "PERSONAL",
  role_key: "technician",
};

const sectionLabel = (value) =>
  SECTION_OPTIONS.find((item) => item.value === value)?.label || "Other";

const frequencyLabel = (value) =>
  FREQUENCY_OPTIONS.find((item) => item.value === value)?.label || "Daily";

const priorityLabel = (value) =>
  PRIORITY_OPTIONS.find((item) => item.value === value)?.label || "Normal";

const priorityClass = (value) => {
  if (value === "HIGH") return "border-rose-200 bg-rose-50 text-rose-700";
  if (value === "LOW") return "border-slate-200 bg-slate-50 text-slate-600";
  return "border-amber-200 bg-amber-50 text-amber-700";
};

const formatSalonDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateValue;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
};

const getSectionOrder = (section) =>
  SECTION_OPTIONS.findIndex((item) => item.value === section);

const normalizeDueTimeToQuarterHour = (value) => {
  if (!value) return "";
  const [hourValue, minuteValue] = String(value).split(":");
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return "";
  const totalMinutes = hour * 60 + minute;
  const rounded = Math.round(totalMinutes / 15) * 15;
  const capped = Math.min(23 * 60 + 45, Math.max(0, rounded));
  const nextHour = Math.floor(capped / 60);
  const nextMinute = capped % 60;
  return `${String(nextHour).padStart(2, "0")}:${String(nextMinute).padStart(2, "0")}`;
};

const getDueTimeParts = (value) => {
  const normalized = normalizeDueTimeToQuarterHour(value);
  if (!normalized) {
    return {
      hour: "",
      minute: "00",
      period: "AM",
    };
  }
  const [hourValue, minuteValue] = normalized.split(":");
  const hour = Number(hourValue);
  return {
    hour: String(hour % 12 || 12).padStart(2, "0"),
    minute: DUE_TIME_MINUTES.includes(minuteValue) ? minuteValue : "00",
    period: hour >= 12 ? "PM" : "AM",
  };
};

const buildDueTimeValue = ({ hour, minute, period }) => {
  if (!hour) return "";
  const hourNumber = Number(hour);
  if (!Number.isFinite(hourNumber)) return "";
  let nextHour = hourNumber % 12;
  if (period === "PM") nextHour += 12;
  const nextMinute = DUE_TIME_MINUTES.includes(minute) ? minute : "00";
  return `${String(nextHour).padStart(2, "0")}:${nextMinute}`;
};

export default function DailyTasks() {
  const { token, role } = useAuth();
  const currentRole = normalizeSalonRole(role);
  const toastRef = useRef(null);
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState("today");
  const [roleFilter, setRoleFilter] = useState(currentRole);
  const [salonTimezone, setSalonTimezone] = useState("America/New_York");
  const [salonToday, setSalonToday] = useState("");
  const [canManageTemplates, setCanManageTemplates] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    role_key: currentRole,
  });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDailyTasks(
        {
          view,
          role_key: roleFilter,
          seed_defaults: true,
        },
        token
      );
      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
      setSalonTimezone(data?.salon_timezone || "America/New_York");
      setSalonToday(data?.salon_today || "");
      setCanManageTemplates(Boolean(data?.can_manage_templates));
      if (data?.role_key && data.role_key !== roleFilter) {
        setRoleFilter(data.role_key);
      }
    } catch (err) {
      setError("Daily tasks could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [roleFilter, token, view]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(
    () => () => {
      if (toastRef.current) clearTimeout(toastRef.current);
    },
    []
  );

  const showToast = useCallback((message) => {
    setToast(message);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => {
      setToast("");
      toastRef.current = null;
    }, 3000);
  }, []);

  const progress = useMemo(() => {
    const activeTasks = tasks.filter((task) => task.is_active !== false);
    const completed = activeTasks.filter((task) => task.is_completed).length;
    return {
      completed,
      total: activeTasks.length,
      percent: activeTasks.length ? Math.round((completed / activeTasks.length) * 100) : 0,
    };
  }, [tasks]);

  const groupedTasks = useMemo(() => {
    const groups = new Map();
    tasks.forEach((task) => {
      const section = task.section || "DURING_DAY";
      if (!groups.has(section)) groups.set(section, []);
      groups.get(section).push(task);
    });
    return Array.from(groups.entries())
      .sort(([a], [b]) => getSectionOrder(a) - getSectionOrder(b))
      .map(([section, items]) => ({
        section,
        label: sectionLabel(section),
        items,
        completed: items.filter((task) => task.is_completed).length,
      }));
  }, [tasks]);

  const dueTimeParts = useMemo(() => getDueTimeParts(form.due_time), [form.due_time]);

  const updateDueTimePart = (part, value) => {
    const nextParts = {
      ...dueTimeParts,
      [part]: value,
    };
    setForm((current) => ({
      ...current,
      due_time: buildDueTimeValue(nextParts),
    }));
  };

  const openAddModal = (scope = "PERSONAL") => {
    const nextScope = canManageTemplates && scope === "ROLE" ? "ROLE" : "PERSONAL";
    setEditingTask(null);
    setForm({
      ...EMPTY_FORM,
      scope: nextScope,
      section: nextScope === "PERSONAL" ? "PERSONAL" : "DURING_DAY",
      role_key: roleFilter || currentRole,
    });
    setFormError("");
    setModalOpen(true);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || "",
      section: task.section || "DURING_DAY",
      frequency_type: task.frequency_type || "DAILY",
      priority: task.priority || "NORMAL",
      due_time: normalizeDueTimeToQuarterHour(task.due_time),
      scope: task.scope || (task.owner_user_id ? "PERSONAL" : "ROLE"),
      role_key: task.role_key || roleFilter || currentRole,
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingTask(null);
    setFormError("");
  };

  const handleSave = async () => {
    const title = form.title.trim();
    if (!title) {
      setFormError("Task name is required.");
      return;
    }
    setSaving(true);
    setFormError("");
    const payload = {
      title,
      section: form.section,
      frequency_type: form.frequency_type,
      priority: form.priority,
      due_time: form.due_time || null,
      role_key: form.role_key,
    };
    try {
      if (editingTask) {
        await updateDailyTask(editingTask.id, payload, token);
        showToast("Task updated.");
      } else {
        await createDailyTask(
          {
            ...payload,
            scope: form.scope,
          },
          token
        );
        showToast("Task added.");
      }
      setModalOpen(false);
      setEditingTask(null);
      await loadTasks();
    } catch (err) {
      setFormError("Task could not be saved. Please check the fields and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (task) => {
    const previous = tasks;
    setTasks((items) =>
      items.map((item) =>
        item.id === task.id ? { ...item, is_completed: !task.is_completed } : item
      )
    );
    try {
      if (task.is_completed) {
        await uncompleteDailyTask(task.id, { occurrence_key: task.occurrence_key }, token);
      } else {
        await completeDailyTask(task.id, { occurrence_key: task.occurrence_key }, token);
      }
      await loadTasks();
    } catch (err) {
      setTasks(previous);
      showToast("Task status could not be changed.");
    }
  };

  const handleMove = async (task, direction) => {
    const delta = direction === "up" ? -15 : 15;
    try {
      await updateDailyTask(task.id, { sort_order: Number(task.sort_order || 0) + delta }, token);
      await loadTasks();
    } catch (err) {
      showToast("Task order could not be changed.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    try {
      await deleteDailyTask(deleteTarget.id, token);
      setDeleteTarget(null);
      showToast("Task removed.");
      await loadTasks();
    } catch (err) {
      showToast("Task could not be removed.");
    } finally {
      setDeleteSaving(false);
    }
  };

  const renderTaskRow = (task) => (
    <div
      key={task.id}
      className={`grid gap-3 border-t border-slate-100 px-4 py-3 md:grid-cols-[auto_1fr_auto] md:items-center ${
        task.is_completed ? "bg-slate-50/80" : "bg-white"
      }`}
    >
      <button
        type="button"
        onClick={() => handleToggleComplete(task)}
        className={`mt-1 flex h-5 w-5 items-center justify-center rounded border text-xs font-bold transition md:mt-0 ${
          task.is_completed
            ? "border-rose-500 bg-rose-500 text-white"
            : "border-slate-300 bg-white text-transparent hover:border-rose-400"
        }`}
        aria-label={task.is_completed ? "Mark task incomplete" : "Mark task complete"}
      >
        ✓
      </button>
      <div className="min-w-0">
        <div
          className={`text-sm font-semibold ${
            task.is_completed ? "text-slate-400 line-through" : "text-slate-900"
          }`}
        >
          {task.title}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>{frequencyLabel(task.frequency_type)}</span>
          {task.due_time ? <span>Due {task.due_time}</span> : null}
          <span>{task.scope === "PERSONAL" ? "My task" : roleLabel(task.role_key)}</span>
          <span className={`rounded-full border px-2 py-0.5 font-semibold ${priorityClass(task.priority)}`}>
            {priorityLabel(task.priority)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 md:justify-end">
        {task.can_edit ? (
          <>
            <button
              type="button"
              onClick={() => handleMove(task, "up")}
              className="h-8 w-8 rounded-full border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              title="Move up"
              aria-label="Move task up"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => handleMove(task, "down")}
              className="h-8 w-8 rounded-full border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              title="Move down"
              aria-label="Move task down"
            >
              ↓
            </button>
            <button
              type="button"
              onClick={() => openEditModal(task)}
              className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setDeleteTarget(task)}
              className="rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
            >
              Delete
            </button>
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact">
        <div
          className={`relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl ${pageText}`}
        >
        <div className="flex flex-col gap-3 bg-rose-500 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <h1 className="text-xl font-semibold text-white md:text-2xl">
            Daily Tasks
          </h1>
          <div className="hidden">
            <p className="mt-2 text-sm text-slate-600">
              {formatSalonDate(salonToday)} · Salon time: {salonTimezone}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canManageTemplates ? (
              <button
                type="button"
                onClick={() => setShowSettings((value) => !value)}
                className={`${headerButton} border border-white/30 bg-white/10 text-white hover:bg-white/20`}
              >
                Task Settings
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => openAddModal("PERSONAL")}
              className={`${headerButton} bg-white text-rose-600 hover:bg-rose-50`}
            >
              Add Task
            </button>
          </div>
        </div>

        <div className="space-y-5 px-6 py-6">
        <p className="text-sm text-slate-600">
          {formatSalonDate(salonToday)} - Salon time: {salonTimezone}
        </p>
        <p className="hidden">
          {formatSalonDate(salonToday)} Â· Salon time: {salonTimezone}
        </p>

        {showSettings && canManageTemplates ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Role templates</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Choose a role to review or customize its salon checklist.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  className="h-10 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700"
                >
                  {ROLE_OPTIONS.map((item) => (
                    <option key={item} value={item}>
                      {roleLabel(item)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => openAddModal("ROLE")}
                  className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                >
                  Add Role Task
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {progress.completed} of {progress.total} complete today
              </div>
              <div className="mt-2 h-2 w-full max-w-md overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-rose-500 transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {VIEW_OPTIONS.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setView(item.value)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    view === item.value
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm font-semibold text-slate-500 shadow-sm">
            Loading daily tasks...
          </div>
        ) : groupedTasks.length ? (
          <div className="space-y-4">
            {groupedTasks.map((group) => (
              <section
                key={group.section}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="flex items-center justify-between px-4 py-3">
                  <h2 className="text-base font-bold text-slate-900">{group.label}</h2>
                  <span className="text-xs font-semibold text-slate-500">
                    {group.completed}/{group.items.length} complete
                  </span>
                </div>
                {group.items.map(renderTaskRow)}
              </section>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">No tasks here yet</h2>
            <p className="mt-2 text-sm text-slate-600">
              Add a task or switch views to see completed work.
            </p>
          </div>
        )}
      </div>
        </div>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-[12000] -translate-x-1/2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <SBINModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingTask ? "Edit Task" : "Add Task"}
        size="lg"
        fitContent
        closeOnBackdrop
        closeOnEscape
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Task"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {formError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              {formError}
            </div>
          ) : null}
          {!editingTask && canManageTemplates ? (
            <div>
              <label className="text-sm font-semibold text-slate-700">Task type</label>
              <select
                value={form.scope}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    scope: event.target.value,
                    section: event.target.value === "PERSONAL" ? "PERSONAL" : "DURING_DAY",
                  }))
                }
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              >
                <option value="PERSONAL">My task</option>
                <option value="ROLE">Role template task</option>
              </select>
            </div>
          ) : null}
          {form.scope === "ROLE" ? (
            <div>
              <label className="text-sm font-semibold text-slate-700">Role</label>
              <select
                value={form.role_key}
                onChange={(event) =>
                  setForm((current) => ({ ...current, role_key: event.target.value }))
                }
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              >
                {ROLE_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {roleLabel(item)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div>
            <label className="text-sm font-semibold text-slate-700">Task name</label>
            <input
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
              className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              placeholder="Review today's bookings"
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-700">Section</label>
              <select
                value={form.section}
                onChange={(event) =>
                  setForm((current) => ({ ...current, section: event.target.value }))
                }
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              >
                {SECTION_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Repeats</label>
              <select
                value={form.frequency_type}
                onChange={(event) =>
                  setForm((current) => ({ ...current, frequency_type: event.target.value }))
                }
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              >
                {FREQUENCY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Priority</label>
              <select
                value={form.priority}
                onChange={(event) =>
                  setForm((current) => ({ ...current, priority: event.target.value }))
                }
                className="mt-1 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
              >
                {PRIORITY_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Due time</label>
              <div className="mt-1 grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                <select
                  value={dueTimeParts.hour}
                  onChange={(event) => updateDueTimePart("hour", event.target.value)}
                  className="h-11 rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
                  aria-label="Due time hour"
                >
                  <option value="">--</option>
                  {DUE_TIME_HOURS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select
                  value={dueTimeParts.minute}
                  onChange={(event) => updateDueTimePart("minute", event.target.value)}
                  className="h-11 rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
                  aria-label="Due time minute"
                  disabled={!dueTimeParts.hour}
                >
                  {DUE_TIME_MINUTES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select
                  value={dueTimeParts.period}
                  onChange={(event) => updateDueTimePart("period", event.target.value)}
                  className="h-11 rounded-xl border border-slate-300 px-3 text-sm text-slate-900"
                  aria-label="Due time period"
                  disabled={!dueTimeParts.hour}
                >
                  {DUE_TIME_PERIODS.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setForm((current) => ({ ...current, due_time: "" }))}
                  className="h-11 rounded-xl border border-slate-300 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </SBINModal>

      <SBINModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete Task"
        size="sm"
        fitContent
        closeOnBackdrop
        closeOnEscape
        footer={
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteSaving}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleteSaving ? "Deleting..." : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Delete <span className="font-semibold">{deleteTarget?.title}</span> from the checklist?
        </p>
      </SBINModal>
    </div>
  );
}
